import Component from '@glimmer/component'
import { service } from '@ember/service'
import { camelize } from '@ember/string'
import { isPresent } from '@ember/utils'
import { reportingLegend as legend } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { basicAverage } from 'teacher-ui-client/utils/giraffe/average-calculator'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type { IntlService } from 'ember-intl'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import type { LegendOptions } from 'highcharts'
import { StatBox } from 'teacher-ui-client/components/giraffe/stat-box'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import type { Box, themes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import type { TotalUsageOverTimeDataItem } from 'teacher-ui-client/utils/student-events/fetchers/total-usage-over-time'

interface Stats {
  title: string
  category: string
  stats: {
    name: string
    value: string
  }[][]
  noClickAction?: boolean
  tooltip?: string
  theme?: keyof typeof themes
}

interface Args {
  data: TotalUsageOverTimeDataItem[]
  boxes: Box[]
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductFp
  tileClickAction?: (category: string) => void
  uiScope: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

function getColor(product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductFp, category: string) {
  switch (`${product}.${category}`) {
    case 'maths.mental_minute':
      return colors.juicyOrange400
    case 'fast_phonics.books':
    case 'maths.quizzes':
    case 'rex.spelling':
    case 're.spelling':
      return colors.grapeyGreen300
    case 'maths.lessons':
    case 're.quizzes_reading':
      return colors.forestGreen300
    case 'fast_phonics.lessons':
    case 're.lessons':
    case 'rex.lessons':
    case 'maths.driving_tests':
      return colors.oceanyBlue300
    case 'fast_phonics.words':
    case 'rex.quizzes':
    case 're.driving_tests':
      return colors.wlBlue250
    case 'rex.assessments':
    case 'fast_phonics.quizzes':
    case 'maths.assessments':
    case 're.assessments':
      return colors.juicyOrange300
    case 'rex.books':
    case 're.books':
    case 'fast_phonics.sounds':
      return colors.purplyPink300
  }
}

export class DashboardTotalUsageOverTime extends Component<Signature> {
  @service intl!: IntlService

  readonly legend: LegendOptions = legend('left', 'top', 40, 10)

  tileClickAction = (statBox: Stats, category: string) => {
    if (statBox.noClickAction) return
    this.args.tileClickAction?.(category)
  }

  // This preserves the order that the stat boxes should be displayed in
  get categories() {
    return this.args.boxes.map((item) => item.category)
  }

  /**
   * Converts the category array an object like { 'lessons': { tooltip: '..', abc: .. }, 'some-other-category':, { .. } }
   * For easier access to category properties
   */
  get keyedBoxes(): Record<string, Record<Box['category'], Box>> {
    return this.args.boxes.reduce((keyed, box) => {
      return { ...keyed, [box.category]: box }
    }, {})
  }

  // Returns an array of stat box objects, each with the data required for a stat box, in the intended display order.
  get statBoxes(): Stats[] {
    const { categories, intl } = this
    const summaryData = this.args.data

    return categories
      .map((category) => {
        const categorySummaryData = summaryData.find((summary) => summary.category === category)

        if (!categorySummaryData) return null

        const { event_count, student_count } = categorySummaryData
        const average = basicAverage(event_count, student_count)

        const camelizedCategory = camelize(category)
        const camelizedProduct = camelize(this.args.product)

        let title = intl.t(`reporting.totalUsageOverTime.titles.${camelizedCategory}.${camelizedProduct}`)
        if (intl.exists(`reporting.totalUsageOverTime.titles.${camelizedCategory}`)) {
          title = intl.t(`reporting.totalUsageOverTime.titles.${camelizedCategory}`)
        }

        const scopeSiteCopyKey = category === 'mental_minute' ? 'sprints' : camelize(this.args.uiScope)

        const firstStatLabel = intl.t(`reporting.statBoxes.totalUsageOverTime.${scopeSiteCopyKey}`)
        const secondStatLabel = intl.t(`reporting.statBoxes.totalUsageOverTime.perStudent`)

        return {
          title,
          category,
          stats: [
            [
              { name: firstStatLabel, value: formatNumber(event_count, 1) },
              { name: secondStatLabel, value: formatNumber(average, 1) },
            ],
          ],
          ...this.keyedBoxes[category],
        }
      })
      .filter(isPresent)
  }

  // Not ideal. But we can't join class strings like `xl:w-1/{{statBoxes.length}}` or the class will not be included.
  // We probably want to come up with a better solution for XL screen sizes though
  get statBoxesXlWidthClass() {
    switch (this.statBoxes.length) {
      case 4:
        return 'xl:w-1/4'
      case 5:
        return 'xl:w-1/5'
      case 6:
        return 'xl:w-1/6'
      default:
        return ''
    }
  }

  // These will be the chart X axis categories, use the months given in the first data item.
  get chartMonths() {
    const monthData = this.args.data
    const extractedMonthData = monthData[0].months
    return extractedMonthData.map(({ month }) => convertDateFormat(month, 'yyyy-MM', 'MMMM'))
  }

  // Format and sort data for the series column chart
  get chartData(): SeriesColumnOptions[] {
    const { categories, intl } = this
    const { product } = this.args
    const monthData = this.args.data

    return categories
      .map((category) => {
        const dataItem = monthData.find((item) => item.category === category)

        if (!dataItem) return null

        const byMonth = dataItem?.months.map((item) => item.count) ?? []

        const camelizedCategory = camelize(category)
        const camelizedProduct = camelize(product)

        let name = intl.t(`reporting.totalUsageOverTime.legends.${camelizedCategory}.${camelizedProduct}`)
        if (intl.exists(`reporting.totalUsageOverTime.legends.${camelizedCategory}`)) {
          name = intl.t(`reporting.totalUsageOverTime.legends.${camelizedCategory}`)
        }
        if (!name) return null

        const color = getColor(product, category)

        return { name, category, data: byMonth, color, type: 'column' as const }
      })
      .filter(isPresent)
  }

  <template>
    <div data-test-total-usage-over-time class="flex h-full flex-col" ...attributes>
      <div class="flex flex-wrap justify-center" data-test-stat-box-container>
        {{#each this.statBoxes as |statBox|}}
          <StatBox
            data-test-stat-box={{statBox.category}}
            class="w-1/3 min-w-[180px] {{this.statBoxesXlWidthClass}} border-r border-b border-white"
            @title={{statBox.title}}
            @tooltipText={{statBox.tooltip}}
            @theme={{statBox.theme}}
            @stats={{statBox.stats}}
            role={{unless statBox.noClickAction "button"}}
            {{on "click" (fn this.tileClickAction statBox statBox.category)}}
            {{! This lint rule doesn't work too nicely with optional interactions }}
            {{! https://github.com/ember-template-lint/ember-template-lint/issues/274 }}
            {{! template-lint-disable no-invalid-interactive }}
          />
        {{/each}}
      </div>
      <total-usage-chart class="relative block basis-96" data-test-total-usage-chart>
        <SeriesColumn
          @data={{this.chartData}}
          @categories={{this.chartMonths}}
          @stackingStyle="normal"
          @legend={{this.legend}}
          @yAxisReversedStacks={{false}}
        />
      </total-usage-chart>
    </div>
  </template>
}

export default DashboardTotalUsageOverTime
